<template>
  <div>
    <image-editor :imgSrc="fileData.imgSrc" v-if="context === 'edit'"></image-editor>
    <file-detail v-if="context === 'detail'" :fileData="fileData"></file-detail>
    <div v-if="context === 'overview'">
      <!-- Row top buttons -->
      <div class="row gutter-sm q-mb-lg">

        <!-- Table buttons -->
        <div class="col-md-12 col-sm-12 col-xs-12 q-pt-sm q-pr-sm" style="justify-content: flex-end">
          <!-- Fileinput for buttonUpload (hidden) -->
          <q-btn-dropdown split label="Actions"
            no-caps
            class="right q-ml-md"
            color="grey-5">
            <q-list link>

              <!-- Upload -->
              <q-item
                :disable="!allowedToDrop"
                :disabled="!activeDir.location || ['library', '/library', ''].includes(activeDir.location)"
                :label="getString('module.library.uploader.btn')"
                icon-right="create_new_folder"
                @click.native="publishLibraryEvent('library:upload')">
                <q-item-main>
                  <q-item-tile label>{{ getString('module.library.uploader.btn') }}</q-item-tile>
                </q-item-main>
              </q-item>

              <!-- Add folder -->
              <q-item
                v-if="canCreateFolder || ['library', '/library', ''].includes(activeDir.location)"
                icon-right="create_new_folder"
                @click.native="publishLibraryEvent('library:new-folder')">
                <q-item-main>
                  <q-item-tile label>{{ getString('module.library.addFolder') }}</q-item-tile>
                </q-item-main>
              </q-item>

              <!-- Rename -->
              <q-item
                :disabled="disableRename"
                icon-right="edit"
                @click.native="publishLibraryEvent('library:rename')">
                <q-item-main>
                  <q-item-tile label>{{ getString('rename') }}</q-item-tile>
                </q-item-main>
              </q-item>

              <!-- Delete -->
              <q-item
                :disabled="isParent || !rowSelection.length"
                @click.native="publishLibraryEvent('library:delete')">
                <q-item-main>
                  <q-item-tile label>{{ getString('action.delete') }}</q-item-tile>
                </q-item-main>
              </q-item>

            </q-list>
          </q-btn-dropdown>
        </div>
      </div>

      <div class="row gutter-sm">
        <!-- Left column -->
        <div class="col-md-4 col-sm-12 col-xs-12">

          <div class="col-md-4 col-sm-12 col-xs-12 q-mb-md">
            <q-input
              :placeholder="getString('module.library.tree.filter.label')"
              v-model="filter"
            />
          </div>

          <div class="card-block tree-block" :class="{'loading': isLoading}">

            <q-tree
              v-if="treeLoaded"
              :filter="filter"
              :nodes="tree"
              color="layout-aside-background"
              node-key="id"
              label-key="name"
              icon="chevron_right"
              ref="tree"
              :selected.sync="selectTree"
              :expanded.sync="expandTree"
            >

              <div slot="header-top" slot-scope="prop" @click="fetchContent(prop.node)" class="cursor-pointer">
                Library folder (root)
              </div>

              <div slot="header-root" @click="fetchContent(prop.node)" slot-scope="prop" class="row items-center branch-parent" :class="{'text-weight-bold' : prop.node.location === activeDir.location }">
                <div class="branch">
                  <q-icon size="large" :name="(prop.node.name === activeDir.name || prop.node.location === `/${activeDir.location}`) ? 'folder_open' : 'folder'" /> {{ prop.node.name }}
                </div>
              </div>

            </q-tree>
          </div>
        </div>
        <!-- Right column -->
        <div class="col-md-8 col-sm-12 col-xs-12 q-pa-sm">
          <library-table :sharedState="true" spacing="293px"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _isEmpty from 'lodash.isempty';
import EventBus from '@/event-bus';

// Helpers
import getString from '@/helpers/GetStrings';

// Mixins
import tableMixin from '@/mixins/tables/tableMixin';
import pagination from '@/mixins/tables/pagination';
import fetchData from '@/mixins/tables/fetchData';
import selectRows from '@/mixins/tables/selectRows';
import upload from '@/mixins/library/upload';

import LibraryTable from '@/components/library/LibraryTable.vue';
import ImageEditor from '../editor/ImageEditor.vue';
import FileDetail from '../detail/LibraryDetail.vue';

export default {
  name: 'Library',

  mixins: [
    tableMixin,
    pagination,
    fetchData,
    selectRows,
    upload,
  ],

  components: {
    LibraryTable,
    ImageEditor,
    FileDetail,
  },

  mounted() {
    EventBus.$on('library-table:modify-rows', () => {
      this.getTree();
    });

    EventBus.$on('library-table:row-selection', (selection) => {
      this.rowSelection = selection.selection;
      this.isParent = selection.parent;
    });

    EventBus.$on('library-table:rowDetail', (selection) => {
      this.rowDetail = selection;
    });

    EventBus.$on('library-table:tree-push', (payload) => {
      if (payload === 'parent') {
        this.activeDir.name = this.expandTree.pop();

        this.expandTree.push(this.activeDir.name);
      } else {
        this.expandTree.push(payload);
      }
    });

    EventBus.$on('library-table:fetch-content', (data) => {
      this.activeDir = data;
      // Cut out string 'api' for the right api call
      // const url = data.location.replace('api', '');
      // this.apiUrl = url;
    });

    this.getTree();
  },

  data() {
    return {
      isParent: false,
      fileData: {
        filename: null,
        imgSrc: null,
      },
      rowDetail: null,
      rowSelection: false,
      context: 'overview',
      contextModal: false,
      tree: [],
      treeLoaded: false,
      expandTree: ['Library folder'], // the root folder should always be open
      selectTree: [], // Selected items
      filter: '',
      dir: {},
      activeDir: {},

      // Property for renaming a file or folder
      propertyName: '',
      targetIsFolder: false,
    };
  },

  computed: {
    canCreateFolder() {
      return this.rowDetail ? this.rowDetail.type === 'regular' : true;
    },

    // Gives back if true if activeDir is empty object
    activeDirEmpty() {
      return _isEmpty(this.activeDir);
    },

    disableRename() {
      // if rowdetail is present it can always be renamed
      if (this.rowDetail) {
        return false;
      }
      // Otherwise the active directory (in the tree) may be renamed
      return !this.activeDir.name || this.activeDir.id === 0;
    },

    allowedToDrop() {
      return this.apiUrl !== '/library';
    },

  },

  methods: {
    getString,

    fetchContent(data) {
      EventBus.$emit('library:fetch-tree', data);
    },

    fetchFileDetails(filename) {
      return new Promise(async (resolve, reject) => {
        try {
          const response = await this.axios.get(`library/${filename}`);
          resolve(response);
        } catch (error) {
          reject(error);
        }
      });
    },

    expandAll() {
      this.$refs.tree.expandAll();
    },

    collapseAll() {
      this.$refs.tree.collapseAll();
    },

    // Function to refresh tree and table
    refreshLibrary() {
      // reset values, get new tree and reset table
      this.tree = [];
      this.getTree();
      this.rowSelection = [];
      EventBus.$emit('refresh-table');
    },

    // Get library-tree from API
    async getTree() {
      // call API to get library-tree
      try {
        const response = await this.axios.get('/library');
        this.processTree(response.data.folder);
      } catch (error) {
        // 404 is also returned when table is empty (why not 204?)
        if (error.response && error.response.status && error.response.status !== 404) {
          // Throw negative notification
          this.$q.notify({
            message: getString('error.general'),
            position: 'top',
            timeout: 2000,
            color: 'negative',
          });
        }
      }
    },

    // process API data for being consumed by Q-tree
    processTree(lib) {
      // Starting values
      const build = [{
        name: 'Library folder', // <-Value visible on top of tree
        id: 0,
        type: 'regular',
        header: 'top',
        location: '/library',
        children: [], // <- Top level tree
      }];
      // Rename properties so q-tree can work with them
      lib = this.rename(lib);
      // Update build with object given by rename function
      build[0].children = lib;
      // Give to data-property to use in q-tree
      this.tree = build;

      this.treeLoaded = true;
    },

    /**
     * This function renames the nested array-keys.
     * Q-tree only accepts the key: children instead of folders
     * */
    rename(data) {
      if (data === null) {
        return null;
      }
      for (let i = 0; i < data.length; i += 1) {
        /**
         * Add header property and give it 'root' as value
         * This is for the Quasar tree component to be able to work with slots
         * and thus add custom html
         */
        data[i].header = 'root';

        data[i].children = [];
        if (data[i].folders !== null) {
          for (let f = 0; f < data[i].folders.length; f += 1) {
            const folder = data[i].folders[f];
            const children = this.rename(folder.folders);
            const child = { id: folder.id, name: `${folder.name} `, header: 'root', location: `${folder.location}`, type: `${folder.type}` };
            if (children !== null) {
              child.children = children;
            }
            data[i].children.push(child);
          }
        }
        delete (data[i].folders);
      }
      // Return tree
      return data;
    },

    publishLibraryEvent(eventName) {
      if (eventName === 'library:delete') {
        if (this.isParent || !this.rowSelection.length) {
          return;
        }
      }
      if (eventName === 'library:rename' && this.disableRename) {
        return;
      }
      if (eventName === 'library:upload') {
        if (!this.allowedToDrop || !this.activeDir.location || ['library', '/library', ''].includes(this.activeDir.location)) {
          return;
        }
      }
      EventBus.$emit(eventName);
    },

  },
  destroyed() {
    EventBus.$off('library-table:row-selection');
    EventBus.$off('library-table:tree-push');
    EventBus.$off('library-table:fetch-content');
    EventBus.$off('library-table:modify-rows');
    EventBus.$off('library-table:rowDetail');
  },
};
</script>

<style lang="stylus">
@import '~variables';
@import '../../../styles/components/tables.general.styl';
@import '../../../styles/components/library.styl';
.detail-modal.modal.maximized .modal-content {
  height: 90%;
  width: 90%;
  border-radius: 4px;
  padding: 2rem;
}
.row.file-header {
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border-bottom: 1px solid #F6F6F6;
}
.file-details {
  padding: 1rem;
  height: 30vh;
  margin-top: 2rem;
  border: 1px solid #F6F6F6;
  border-radius: 4px;
}
</style>
